import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { setAddMultyPopup } from "features/alert/alertSlice";
import { setRefresh } from "features/geoFence/geoFenceSlice";
import { setAlertConfiguration } from "config/apis";
import { fetchAlertConfigDetails } from "features/alert/alertThunk";


export default function AddMultyAlertDialog() {
  const dispatch = useDispatch();
  const addMultiPopup = useSelector((state) => state.alerts.addMultiPopup);
  const alertData = useSelector((state) => state.alerts.alertData);
  const selectedRows = useSelector((state) => state.alerts.selectedRows);
  const selectedAlert = useSelector((state) => state.alerts.selectedAlert);

  const [alertValue, setAlertValue] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [mobileInput, setMobileInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [mobileNumbers, setMobileNumbers] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const ignitionAlertIds = ["14", "9", "6", "10", "7", "8"];

  const isIgnitionAlert = ignitionAlertIds.includes(
    String(selectedAlert?.alertId)
  );

  const isDateAlert = String(selectedAlert?.alertId) === "15";

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleClose = () => dispatch(setAddMultyPopup(false));

  /* ---------------- Validation ---------------- */

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidMobile = (mobile) =>
    /^[0-9]{10}$/.test(mobile);

  /* ---------------- Add Mobile ---------------- */

  const addMobile = () => {
    const trimmed = mobileInput.trim();

    if (!trimmed) return;

    if (!isValidMobile(trimmed)) {
      setSnackbar({
        open: true,
        message: "Mobile number must be 10 digits",
        severity: "error",
      });
      return;
    }

    if (mobileNumbers.includes(trimmed)) {
      setSnackbar({
        open: true,
        message: "Mobile number already added",
        severity: "warning",
      });
      return;
    }

    setMobileNumbers([...mobileNumbers, trimmed]);
    setMobileInput("");
  };

  const handleAddMobile = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addMobile();
    }
  };

  const handleDeleteMobile = (num) => {
    setMobileNumbers(mobileNumbers.filter((item) => item !== num));
  };

  /* ---------------- Add Email ---------------- */

  const addEmail = () => {
    const trimmed = emailInput.trim();

    if (!trimmed) return;

    if (!isValidEmail(trimmed)) {
      setSnackbar({
        open: true,
        message: "Please enter valid Email ID",
        severity: "error",
      });
      return;
    }

    if (emails.includes(trimmed)) {
      setSnackbar({
        open: true,
        message: "Email already added",
        severity: "warning",
      });
      return;
    }

    setEmails([...emails, trimmed]);
    setEmailInput("");
  };

  const handleAddEmail = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleDeleteEmail = (email) => {
    setEmails(emails.filter((item) => item !== email));
  };

  /* ---------------- Submit ---------------- */

  const handleSubmit = async () => {
    if (!isIgnitionAlert && !alertValue) {
  setSnackbar({
    open: true,
    message: isDateAlert ? "Please select a date" : "Alert value is required",
    severity: "warning",
  });
  return;
}

    if (mobileNumbers.length === 0 && emails.length === 0) {
      setSnackbar({
        open: true,
        message: "Add at least one Mobile or Email",
        severity: "warning",
      });
      return;
    }

    if ((startTime && !endTime) || (!startTime && endTime)) {
      setSnackbar({
        open: true,
        message: "Please provide both Start Time and End Time, or leave both empty.",
        severity: "error",
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const payloads = selectedRows
        .map((row) => {
          const alert = alertData.find(
            (item) => item.thingId === row.thingId
          );
          if (!alert) return null;

          return {
            thingId: alert.thingId,
            // alertId: alert.alertId,
            alertID: Number(alert.alertId),
            alertValue: isIgnitionAlert ? "" : alertValue,
            mobileNumber: mobileNumbers,
            emailId: emails,
            alertValueOption: true,
            alertEnabled: true,
            startTime: isDateAlert ? "" : startTime, // Added
            endTime: isDateAlert ? "" : endTime,
          };
        })
        .filter(Boolean);

      if (payloads.length === 0) {
        setSnackbar({
          open: true,
          message: "No alerts selected",
          severity: "warning",
        });
        return;
      }

      const response = await axios.post(
        setAlertConfiguration,
        payloads,
        {
          headers: { "x-dhundhoo-session": token },
        }
      );

      const apiMessage =
        response?.data?.responseData?.[0]?.message ||
        response?.data?.message ||
        "Alerts updated successfully";

      setSnackbar({
        open: true,
        message: apiMessage,
        severity: "success",
      });

      dispatch(setRefresh());
      setTimeout(async () => {
        await dispatch(fetchAlertConfigDetails(alert?.alertId));
      }, 2000);
      handleClose();
      handleClear();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to submit alerts",
        severity: "error",
      });
    }
  };

  const handleClear = () => {
    setAlertValue("");
    setEmailInput("");
    setMobileInput("");
    setEmails([]);
    setMobileNumbers([]);
    setStartTime("");
    setEndTime("");
  };

  return (
    <>
      <Dialog open={addMultiPopup} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          Add Alert Configuration
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isDateAlert ? (
            <TextField
              fullWidth
              margin="normal"
              label="Select Date"
              type="date"
              value={alertValue}
              onChange={(e) => setAlertValue(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          ) : !isIgnitionAlert ? (
            <TextField
              fullWidth
              margin="normal"
              label="Alert Value"
              value={alertValue}
              onChange={(e) => setAlertValue(e.target.value)}
              required
            />
          ) : null}

          {/* Mobile */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                fullWidth
                label="Add Mobile Number"
                value={mobileInput}
                onChange={(e) =>
                  setMobileInput(e.target.value.replace(/\D/g, ""))
                }
                onKeyDown={handleAddMobile}
                inputProps={{ maxLength: 10 }}
              />
              <IconButton
                onClick={addMobile}
                sx={{
                  bgcolor: "#2E7BEF",
                  color: "white",
                  "&:hover": { bgcolor: "#1a5ed8" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {mobileNumbers.map((num, idx) => (
                <Chip
                  key={idx}
                  label={num}
                  onDelete={() => handleDeleteMobile(num)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          {/* Email */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                fullWidth
                label="Add Email ID"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={handleAddEmail}
              />
              <IconButton
                onClick={addEmail}
                sx={{
                  bgcolor: "#2E7BEF",
                  color: "white",
                  "&:hover": { bgcolor: "#1a5ed8" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {emails.map((email, idx) => (
                <Chip
                  key={idx}
                  label={email}
                  onDelete={() => handleDeleteEmail(email)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          {!isDateAlert && (
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Time"
                type="time"
                fullWidth
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleSubmit} variant="contained" style={{ color: "#fff" }}>
            Submit
          </Button>
          <Button onClick={handleClear} variant="outlined" style={{ color: "black" }}>
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
